import * as cc_helpers from "./configChecker_helpers";
import helpers from "./helpers.js";
import immediateApi from "../../api/immediateApi";
const _style = window._style;

class StepDef {
  constructor(action) {
    this.action = action;
  }

  execute(props) {
    this.action(props);
  }
}

class TestDef {
  constructor({
    info: { friendlyName, desc, precedence },
    entryStep, // StepDef
    validatesOnSuccess,
    devalidatesOnFail = () => [],
  }) {
    this.friendlyName = friendlyName;
    this.precedence = precedence;
    this.desc = desc;

    this.entryStep = entryStep;

    this.validatesOnSuccess = validatesOnSuccess;
    this.devalidatesOnFail = devalidatesOnFail;
  }
}

/**
 * @classdesc Represents the runnin of a single test given its definition on the given parameters
 */
class Runner {
  constructor({ testDef, ctx = {}, actuator, runListener }) {
    this.testDef = testDef;
    this.result = {};
    this.ctx = ctx;
    this.actuator  = actuator;
    this.notifier = {
      started: () => { runListener.started(); },
      finished: () => { runListener.finished(); }
    };
    this._reports = {
      pass: (message) => {
        this.result.status = 'passed';
        this.result.message = message;
      },
      fail: ({message, tooltips, errorCode}) => {
        this.result.status = 'failed';
        this.result.message = message;
        this.result.resolution = { description: "" };
        this.result.tooltips = tooltips;    // Note AJ: Tooltip is never used in any of our case but leaving it here as we might need it in the future.
        this.result.errorCode = errorCode;
      },
      diagnose: ({ error, resolution, tooltips, errorCode, test }) => {
        this.result.status = 'diagnose';
        this.result.error = error;
        this.result.resolution = resolution;
        this.result.tooltips = tooltips;
        this.result.errorCode = errorCode;
        this.result.testNum = this.testDef.precedence;
      },
      autofix: ({ message, category }) => {
        this.result.status = 'autofix';
        this.result.message = message;
        this.result.resolution = { description: "Automatically fixed." };
        this.result.category = category;
        this.result.testNum = this.testDef.precedence;
      },
    };
  }



  runStep(step) {
    const rVal = step.execute({
      runtime: this,
      actuator: this.actuator,
      notifier: this.notifier,
      ...this._reports,
    });
  }

  launch() {
    if (!this.testDef.entryStep) { console.error("given test def does not have an initial step"); return; }
    immediateApi.getSmartDriveStatus(this.actuator.parameters.controllerId)
      .then(() => { this.runStep(this.testDef.entryStep); })
      .catch(cc_helpers.configCheckerOnErrorCallback);
  }
}

class TestPlanProgression {
  constructor(pTestPlan, pActuator) {
    this.activeTest;
    this.testPlan = pTestPlan;
    this.completedTestDefs = new Set();
    this.pendingTestDefs = new Set();
    this.failedTestDef = null;
    this.activeTestDef = null;
    this.actuator = pActuator;
    this.diagnoses = [];
  }

  setActiveTest(testDef) { this.activeTestDef = testDef; }
  getActiveTest() { return this.activeTestDef; }

  completedTest(testDef) {
    this.completedTestDefs.add(testDef);
    if (this.failedTestDef === testDef) { this.failedTestDef = null; }
    this.pendingTestDefs.delete(testDef);
  }
  didCompleteTest(testDef) { return this.completedTestDefs.has(testDef); }

  failedActiveTest() {
    this.failedTestDef = this.activeTestDef;
    const devalidatedValidatables = this.activeTestDef.devalidatesOnFail(this.actuator);

    this.completedTestDefs.forEach(testDef => {
      if (devalidatedValidatables.includes(testDef.validatesOnSuccess(this.actuator))) {
        this.pendingTestDefs.add(testDef);
      }
    });
  }

  isPending(testDef) {
    return this.pendingTestDefs.has(testDef);
  }
  hasPending() { return this.pendingTestDefs.size; }

  getPending() {
    return this.testPlan.flatMap(testDef => this.pendingTestDefs.has(testDef) ? [testDef] : []);
  }

  hasNextTest() {
    return this.testPlan.some(testDef => !this.completedTestDefs.has(testDef) || this.pendingTestDefs.has(testDef));
  }

  getNextTest() {
    if (this.failedTestDef) { // there is a test that was failed
      return this.failedTestDef;
    }
    const firstPendingTestDef = this.testPlan.find(testDef => this.pendingTestDefs.has(testDef));
    if (firstPendingTestDef) { return firstPendingTestDef; }

    const position = this.testPlan.indexOf(this.activeTestDef);
    if (position === this.testPlan.length - 1) { return null; } // at the end of the test plan
    return this.testPlan.find(testDef => !this.completedTestDefs.has(testDef));
  }
}

class Actuator {

  constructor(configAxisObj, pCallback) {
    this.parameters = configAxisObj.parameters;
    this.driveNumber = helpers.getAxisNumberFromColor(this.parameters.axisColor); // expects parentDrive to be its axis number
    this.children = this.parameters.childrenDrives;
    this.homeport = undefined;  // set by function updateEndStopPorts()
    this.endport = undefined;   // set by function updateEndStopPorts()
    cc_helpers.updateEndStopPorts(this, undefined, pCallback);

    // set up validatables
    this.driveNumberValidatable = {
      isValid: false,
      name: `Drive number(s)`
    };

    this.directionValidatable =  {
      isValid: false,
      name: `Motion Direction`,
    };


    this.homeSensorValidatable = {
      isValid: false,
      name: `Home sensor`
    };

    this.endSensorValidatable = {
      isValid: false,
      name: `End sensor`
    };
  }

  // convenience
  isMultiDrive() {
    return this.parameters.childrenDrives.length > 0;
  }

  getAllActuatorDriveNumbers() {
    return [this.driveNumber, ...this.getAllChildDriveNumbers(), ];
  }

  getAllChildDriveNumbers() {
    return this.children.map(child => helpers.convert(child.color).toNumber());
  }

  update(pConfigAxisObj, pCallback) {
    this.parameters = pConfigAxisObj.parameters;
    this.driveNumber = helpers.getAxisNumberFromColor(this.parameters.axisColor);
    this.children = this.parameters.childrenDrives;

    cc_helpers.updateEndStopPorts(this, undefined, pCallback);
  }

}

let isMachineMotionRunning = false;

// MotionMonitor Parameters
const GRACE_PERIOD = 1000; // in ms
const BAD_SPEED_WINDOW = 500; // in ms
const THRESHOLD_SPEED = 1; // in mm/s
const BAD_DIRECTION_DISPLACEMENT_THRESHOLD = 1; // in mm

const motionMonitorErrors = {
  badDirection : 'direction_fault',
  lowSpeed : 'speed_fault'
};
class MotionMonitor {
  constructor(pActuator, pInitialPos, pJogDistance, pCallback) {
    this.actuator = pActuator;
    this.initialPosition = pInitialPos;
    this.jogDistance = pJogDistance;
    this.callback = pCallback;
    this.wasStarted = false;
    this.wasStopped = false;
    isMachineMotionRunning = true; // global flag
    if(window.assembler){
      window.assembler.get3DContext().registerForNotification('config_checker_unmounted', ()=> {
        this.kill();
        this.wasStopped = true;
        isMachineMotionRunning = false;
      });
    }
  }

  start() {
    if (this.wasStarted) { console.warn("already started"); return; }
    this.wasStarted = true;


    const lStartTime = Date.now();
    const lSpeedAlarms = {};
    this.actuator.getAllActuatorDriveNumbers().forEach(pDrive => lSpeedAlarms[pDrive] = { startTime: null, });

    const stopPolling = helpers.throttlePoll( resolve => {
      if (this.wasStopped) { stopPolling(); return; }

      immediateApi.getAllCurrentSpeedsForController(this.actuator.parameters.controllerId).then((pSpeeds) => {
        immediateApi.getAllPositionsForController(this.actuator.parameters.controllerId).then((pPositions) => {

          if (pSpeeds.error || pPositions.error) {
            _style.messageBox('Error getting position and speed.', 'error');
            cc_helpers.configCheckerOnErrorCallback();
            return;
          }

          const lSpeeds = pSpeeds['actual speed'];
          if (this.wasStopped) { return; }

          const lNow = Date.now();
          this.actuator.getAllActuatorDriveNumbers().some(pDrive => {
            if (lNow - lStartTime >= GRACE_PERIOD) { // we are past the grace period
              // Speed Check
              if (Math.abs(lSpeeds[pDrive]) < THRESHOLD_SPEED) {
                if (!lSpeedAlarms[pDrive].startTime) { console.warn("started alarm!"); lSpeedAlarms[pDrive].startTime = lNow; }
                else {
                  if (lNow - lSpeedAlarms[pDrive].startTime > BAD_SPEED_WINDOW) {
                    console.error("alerting!");
                    this.callback(motionMonitorErrors.lowSpeed);
                    return true; // break
                  } else {
                    console.warn(`alarm has been on for ${(lNow - lSpeedAlarms[pDrive].startTime) / 1000} s`);
                  }
                }
              } else {
                lSpeedAlarms[pDrive].start = null;
              }

              // DisplacementCheck
              if (this.initialPosition != undefined) {
                const lTrueAxis = helpers.convert(pDrive).toLetter().toUpperCase();
                if (this.jogDistance > 0) {
                  if (this.initialPosition[lTrueAxis] - pPositions[lTrueAxis] > BAD_DIRECTION_DISPLACEMENT_THRESHOLD) {
                    console.error('bad direction');
                    this.callback(motionMonitorErrors.badDirection);
                    return;
                  }
                }
                else if (this.jogDistance < 0) { // Note Audrey : jogDistance can be undefined
                  if (this.initialPosition[lTrueAxis] - pPositions[lTrueAxis] < -1 * BAD_DIRECTION_DISPLACEMENT_THRESHOLD) {
                    console.error('bad direction');
                    this.callback(motionMonitorErrors.badDirection);
                    return;
                  }
                }
              }
            }
          });
        }).catch(cc_helpers.configCheckerOnErrorCallback);
      }).catch(cc_helpers.configCheckerOnErrorCallback);

      resolve();
    });
  }

  kill () {
    this.wasStopped = true;
    isMachineMotionRunning = false;
  }
}

class Logger {
  constructor() {
    this.storage;
    this.echoes;
    this.consumer;
    this.innateStorage = [];
    this.echoes = false;
  }

  echo(consumer = console.log) {
    this.echoes = true;
    this.consumer = consumer;
  }

  log(message, storage = this.innateStorage) {
    const formatted = `${new Date().toISOString()} : ${message}`;
    storage.push(formatted);
    if (this.echoes) {
      this.consumer("Logger: " + formatted);
    }
  }

  getLogs() {
    return this.innateStorage;
  }

  serialize() {
    return JSON.stringify(this.innateStorage);
  }

  dump(nLast = this.innateStorage.length, consumer = console.log) {
    consumer(this.innateStorage.slice(-nLast).join('\n'));
  }
}

export {
  StepDef,
  TestDef,
  Runner,
  TestPlanProgression,
  Actuator,
  Logger,
  isMachineMotionRunning,
  motionMonitorErrors,
  MotionMonitor
};
